import React from 'react';
import Container from '../layout/Container';
import Eyebrow from '../ui/Eyebrow';
import SectionHeading from '../ui/SectionHeading';
import { colors, font } from '../../lib/tokens';

// Faithful excerpts from an on-the-record interview (published with consent).
// Point 1 becomes the lead pull-quote; points 2 & 3 become labelled notes.
const NOTES = [
  {
    label: 'Cognitive load',
    body: 'It reduces cognitive load. Previously I often had to search the web to find the correct diagnosis code.',
  },
  {
    label: 'Coding quality',
    body: 'It leads to more accurate documentation and coding. Sometimes it suggests diagnosis codes I was not previously aware of, but which turn out to be a better match for the case.',
  },
];

const STATS = [
  { value: '~30', label: 'ED notes written per shift' },
  { value: '30 min+', label: 'Saved per shift (her estimate)' },
];

export default function TestimonialSection() {
  return (
    <section
      id="testimonial"
      className="kc-section"
      style={{
        background: colors.surface,
        borderTop: `1px solid ${colors.hair}`,
      }}
    >
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-[0.95fr_1.3fr] gap-12 md:gap-24">
          {/* Left — framing, attribution, and her own numbers */}
          <div data-reveal>
            <Eyebrow>In practice</Eyebrow>
            <SectionHeading style={{ marginBottom: 24 }}>
              Tested where
              {' '}<br />
              seconds count.
            </SectionHeading>
            <p style={{ color: colors.muted, fontSize: 16, lineHeight: 1.65, maxWidth: 380, margin: 0 }}>
              An emergency-medicine specialist at Landspítali has been coding with KatlaCode on live
              shifts. Here is what changed.
            </p>

            {/* Attribution */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 32 }}>
              <span
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 999,
                  background: colors.blueSoft,
                  color: colors.blue,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: font.sans,
                  fontSize: 15,
                  fontWeight: 600,
                  letterSpacing: -0.3,
                  flexShrink: 0,
                }}
              >
                UÓ
              </span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 500, color: colors.ink }}>
                  Unnur Ósk Stefánsdóttir
                </div>
                <div style={{ fontSize: 13, color: colors.muted, marginTop: 3, lineHeight: 1.45 }}>
                  Emergency Medicine Specialist · Landspítali
                </div>
              </div>
            </div>

            {/* Her own figures */}
            <div
              style={{
                display: 'flex',
                gap: 40,
                marginTop: 36,
                paddingTop: 28,
                borderTop: `1px solid ${colors.hair}`,
              }}
            >
              {STATS.map((s) => (
                <div key={s.label}>
                  <div
                    style={{
                      fontFamily: font.sans,
                      fontSize: 32,
                      fontWeight: 500,
                      letterSpacing: -1,
                      color: colors.ink,
                      lineHeight: 1,
                    }}
                  >
                    {s.value}
                  </div>
                  <div style={{ fontSize: 12.5, color: colors.muted, marginTop: 8, lineHeight: 1.5, maxWidth: 150 }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — the quote itself */}
          <div data-reveal style={{ '--d': '80ms' } as React.CSSProperties}>
            <blockquote style={{ margin: 0 }}>
              <p
                className="text-balance"
                style={{
                  fontFamily: font.sans,
                  fontSize: 'clamp(22px, 2.8vw, 30px)',
                  lineHeight: 1.32,
                  letterSpacing: -0.8,
                  fontWeight: 500,
                  color: colors.ink,
                  margin: 0,
                  // Hanging punctuation: the opening quote sits in the gutter
                  // so the words themselves align with the notes below.
                  textIndent: '-0.4em',
                }}
              >
                “It’s quite accurate, and the correct diagnosis is{' '}
                {/* A hand-drawn underline sketches itself under "always" once
                    the quote has faded in. */}
                <span className="kc-scribble" style={{ color: colors.blue }}>
                  always
                  <svg
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                    aria-hidden
                    className="kc-draw"
                    style={{ '--kc-draw-d': '750ms', '--kc-draw-dur': '700ms' } as React.CSSProperties}
                  >
                    <path
                      d="M2 6.5C22 3.4 42 8.2 62 5.2S88 3.6 98 5.6"
                      pathLength={1}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                    />
                  </svg>
                </span>{' '}
                included in the list of
                suggestions. The system is very useful in daily clinical practice.”
              </p>
            </blockquote>

            {/* Supporting points */}
            <div style={{ marginTop: 44 }}>
              {NOTES.map((n, i) => (
                <div
                  key={n.label}
                  className="kc-label-row"
                  style={{
                    '--kc-label-w': '140px',
                    padding: '24px 0',
                    borderTop: `1px solid ${colors.hair}`,
                    borderBottom: i === NOTES.length - 1 ? `1px solid ${colors.hair}` : 'none',
                  } as React.CSSProperties}
                >
                  <div
                    className="kc-label-row-label"
                    style={{
                      fontFamily: font.mono,
                      fontSize: 11,
                      color: colors.blue,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                    }}
                  >
                    {n.label}
                  </div>
                  <p style={{ color: colors.ink2, fontSize: 16, lineHeight: 1.6, margin: 0 }}>
                    {n.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
